import type React from 'react';
import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { User } from 'lucide-react';
// eslint-disable-next-line import-x/no-named-as-default
import h337 from 'heatmap.js';
import type { PlayerStatistics } from '@/types/statistics';
import { REPORT_SKIN } from '@/lib/utils';
import {
  RAINBOW_GRADIENT,
  type HeatGradient,
  type PlayerColor,
} from './playerColors';
import { classifyShot, selectShots } from './shotTypes';
import {
  courtShowsHeat,
  courtShowsShots,
  courtShowsShotZones,
  type CourtMode,
} from './courtModes';
import {
  destinationPercents,
  resolveShotZone,
  zoneShotCount,
  type DestinationAxis,
} from './shotZones';

/*
 * FULL padel court, landscape (broadcast view).
 *
 * Real court 10 m × 20 m → SVG 200 (length) × 100 (width) at 10 u/m, plus a
 * 10-unit margin. Net is vertical down the centre.
 *
 * Orientation is anchored by a fixed broadcast CAMERA at the left back wall.
 * Each team keeps its real half: team1 plays the LEFT half, team2 the RIGHT.
 * The focused player's movement heat sits on their own half; their shot
 * landings sit on the opponent's half. Switching to an opponent therefore
 * flips the sides — so you can read who played where, against the camera.
 *
 * Depth y ∈ [0,10] is net=0 → back wall=10. We map net to court centre and
 * the back wall to the outer end of the player's half.
 */

// CourtMode lives in ./courtModes (kept import-light for unit tests); re-export
// it here so existing call sites keep importing it from SmartCourt.
export type { CourtMode };
export type CourtSide = 'left' | 'right';

const M = 10;
const HALF_L = 100;
const FULL_L = 200;
const W = 100;
const VB_W = FULL_L + M * 2; // 220
const VB_H = W + M * 2; // 120
const NET_X = M + HALF_L; // 110
const X1 = M + FULL_L; // 210

// own-half depth: net(0) → court centre, back(10) → outer wall of that half
const ownDepthX = (side: CourtSide, y: number) =>
  side === 'left' ? NET_X - y * 10 : NET_X + y * 10;
// opponent-half depth (where this player's shots land)
const oppDepthX = (side: CourtSide, y: number) =>
  side === 'left' ? NET_X + y * 10 : NET_X - y * 10;
// Lateral x ∈ [-5,5] across the width, in the player's OWN perspective. The
// right-half team faces the other way — a 180° rotation that flips BOTH axes —
// so we negate lateral for them, mirroring what ownDepthX already does for
// depth. Without this the right team's left/right (heat, shots, zones, markers)
// would render mirrored. Depth 0=net is shared, so depth needs no extra flip.
const sideLat = (side: CourtSide, x: number) => (side === 'right' ? -x : x);
const latY = (side: CourtSide, x: number) => M + (sideLat(side, x) + 5) * 10;

// Movement-heat projection — mirrors the original BallMap. Laterally the data
// is held 10% off each side wall (origin used left:10% / width:80%); in depth
// it fills the whole half (net → back wall, no inset). Depth y is taken raw
// (0 = net → 10 = back wall): that matches the court_zone front/back split, so
// we do NOT apply mirror_negative_y here (the old portrait court needed it for
// its flipped viewBox; this landscape court does not).
const LAT_PAD = 12; // ~12% off each side wall (origin used 10%)
const BACK_PAD = 8; // hold the deepest cells just inside the back wall
const ownDepthXin = (side: CourtSide, depth: number) => {
  const reach = HALF_L - BACK_PAD;
  return side === 'left'
    ? NET_X - (depth / 10) * reach
    : NET_X + (depth / 10) * reach;
};
const latYin = (side: CourtSide, lat: number) => {
  const span = W - LAT_PAD * 2;
  return M + LAT_PAD + ((sideLat(side, lat) + 5) / 10) * span;
};

type HeatmapData = NonNullable<PlayerStatistics['heatmap']>;

// Centre of grid cell (row i = depth, col j = lateral) in court metres, using
// the backend bin edges. Depth 0 = net → 10 = back wall.
function cellMeters(
  hm: HeatmapData,
  i: number,
  j: number,
): { depth: number; lat: number } {
  const cy = (hm.y_edges[i] + hm.y_edges[i + 1]) / 2;
  const cx = (hm.x_edges[j] + hm.x_edges[j + 1]) / 2;
  return { depth: cy, lat: cx };
}

// Hex mirrors of the theme tokens (--accent / --muted-2 / --red), kept as hex
// so shot markers can be blended toward a player/team color in JS.
const RESULT_COLORS: Record<string, string> = {
  // Black and neutral gray in the Matchlytics skin (its --accent / --muted-2).
  bounce: REPORT_SKIN === 'matchlytics' ? '#0a0a0a' : '#1aa7e0',
  interception: REPORT_SKIN === 'matchlytics' ? '#a3a3a3' : '#8b95a4',
  error: '#dc2626',
  // The winner is the shot worth finding on this court, so unlike the other
  // three it is meant to shout: the brightest, most saturated step of the
  // green ramp, drawn larger and haloed (see the marker below).
  winner: '#12d95f',
};

// How far a shot marker leans toward its entity's identity color when several
// share the court. The shape (disc / ring / X) already carries the outcome, so
// the color leans mostly to identity — enough to tell players'/teams' hits
// apart at a glance, while a residual outcome lean (warm errors, cool bounces)
// keeps the legend readable.
const IDENTITY_TINT = 0.85;

/**
 * The colour one shot mark is painted, and the single source of truth for it —
 * the court draws with this and the legend swatches read from it, so a legend
 * can never claim a colour the court didn't use.
 *
 * `identity` is the owner's hue, passed only when several entities share the
 * court; a lone entity keeps the pure outcome colour.
 *
 * Winners lean like every other mark. They used to be exempt, to protect the
 * green — but that made every winner on court identical no matter who hit it,
 * which is the one thing a reader wants to know when comparing players. The
 * halo carries "winner" on its own; colour is for identity.
 */
export function shotMarkColor(cat: string, identity?: string): string {
  const base = RESULT_COLORS[cat] ?? RESULT_COLORS.bounce;
  if (!identity) return base;
  return mixHex(base, identity, IDENTITY_TINT);
}

/** Blend two #rrggbb colors; t=0 → a, t=1 → b. */
function mixHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const lerp = (shift: number) => {
    const ca = (pa >> shift) & 255;
    const cb = (pb >> shift) & 255;
    return Math.round(ca + (cb - ca) * t);
  };
  const r = lerp(16);
  const g = lerp(8);
  const bl = lerp(0);
  return `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)}`;
}

// ── Court chrome ─────────────────────────────────────────────────────────
function CourtFrame() {
  const x0 = M;
  const x1 = X1;
  const y0 = M;
  const y1 = M + W;
  // Service line 3 m from each back wall (7 m from the net).
  const svcL = x0 + 30;
  const svcR = x1 - 30;
  const midY = M + W / 2;
  const line = 'var(--court-line)';
  const post = 'var(--court-post)';

  // Enclosure post caps — the uprights that hold a padel court's glass + mesh
  // panels. Each 10 m wall segment carries 4 evenly-spaced caps. The structural
  // points bounding a segment — the corners and the net posts — are PHANTOM:
  // they reserve their slot so the caps space evenly *around* them, but carry
  // no cap, leaving a clean gap at each. Side walls split at the net into two
  // halves; back walls run corner → corner. 4 caps × 6 segments = 24.
  const CAPS_PER_SEG = 4;
  const segments: [number, number, number, number][] = [
    [x0, y0, NET_X, y0], // top wall — left-corner → net half
    [NET_X, y0, x1, y0], // top wall — net → right-corner half
    [x0, y1, NET_X, y1], // bottom wall halves
    [NET_X, y1, x1, y1],
    [x0, y0, x0, y1], // left back wall
    [x1, y0, x1, y1], // right back wall
  ];
  const wallPosts: [number, number][] = [];
  for (const [ax, ay, bx, by] of segments) {
    for (let i = 1; i <= CAPS_PER_SEG; i++) {
      const t = i / (CAPS_PER_SEG + 1); // phantom endpoints at t = 0 and t = 1
      wallPosts.push([ax + (bx - ax) * t, ay + (by - ay) * t]);
    }
  }

  return (
    <g>
      <rect x={x0} y={y0} width={FULL_L} height={W} fill="url(#courtSurface)" />
      <rect
        x={x0}
        y={y0}
        width={FULL_L}
        height={W}
        fill="none"
        stroke={line}
        strokeWidth="0.9"
        className="draw-line draw-1"
      />
      <line
        x1={svcL}
        y1={y0}
        x2={svcL}
        y2={y1}
        stroke={line}
        strokeWidth="0.7"
        className="draw-line draw-3"
      />
      <line
        x1={svcR}
        y1={y0}
        x2={svcR}
        y2={y1}
        stroke={line}
        strokeWidth="0.7"
        className="draw-line draw-3"
      />
      <line
        x1={svcL}
        y1={midY}
        x2={NET_X}
        y2={midY}
        stroke={line}
        strokeWidth="0.7"
        className="draw-line draw-4"
      />
      <line
        x1={NET_X}
        y1={midY}
        x2={svcR}
        y2={midY}
        stroke={line}
        strokeWidth="0.7"
        className="draw-line draw-4"
      />
      {/* Net */}
      <line
        x1={NET_X}
        y1={y0}
        x2={NET_X}
        y2={y1}
        stroke={line}
        strokeWidth="1.3"
        className="draw-line draw-2"
      />
      {/* Post caps last, so they sit on top of the wall line. Small rounded
          squares in a soft slate — quiet structural marks, not bright targets. */}
      {wallPosts.map(([cx, cy], i) => {
        const s = 2.2; // cap side length
        // Nudge each cap outward off the playing area so it rests just outside
        // its wall line rather than straddling it (into the field).
        const off = s / 2;
        const px = cx === x0 ? cx - off : cx === x1 ? cx + off : cx;
        const py = cy === y0 ? cy - off : cy === y1 ? cy + off : cy;
        return (
          <rect
            key={`post-${i}`}
            className="court-post"
            x={px - s / 2}
            y={py - s / 2}
            width={s}
            height={s}
            rx={0.7}
            fill={post}
            stroke={line}
            strokeWidth={0.4}
          />
        );
      })}
    </g>
  );
}

// ── heatmap.js movement layer (canvas overlay over the player's own half) ──
type HeatInstance = {
  setData: (d: {
    max: number;
    min: number;
    data: { x: number; y: number; value: number }[];
  }) => void;
};

function HeatLayer({
  heatmap,
  side,
  gradient,
  maxOpacity,
}: {
  heatmap: HeatmapData;
  side: CourtSide;
  gradient: HeatGradient;
  maxOpacity: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inst = useRef<HeatInstance | null>(null);
  const grid = heatmap.heatmap;

  useEffect(() => {
    const el = ref.current;
    if (!el || !grid) return;
    let max = 0;
    for (const row of grid) for (const v of row) if (v > max) max = v;

    const build = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (!w || !h || max <= 0) return;
      el.innerHTML = '';
      const config = {
        container: el,
        radius: Math.max(16, Math.round(w / 15)),
        maxOpacity,
        minOpacity: 0,
        blur: 0.85,
        gradient,
      };
      // eslint-disable-next-line import-x/no-named-as-default-member
      inst.current = h337.create(config) as unknown as HeatInstance;
      const pts: { x: number; y: number; value: number }[] = [];
      // The canvas covers the WHOLE court-wrap; map each cell (via its backend
      // bin edges + mirror flag) through the inset half-court projection so the
      // dense edge cells sit just inside the lines rather than spilling over.
      for (let i = 0; i < grid.length; i++) {
        for (let j = 0; j < grid[i].length; j++) {
          const v = grid[i][j];
          if (v <= 0) continue;
          const { depth, lat } = cellMeters(heatmap, i, j);
          const px = (ownDepthXin(side, depth) / VB_W) * w;
          const py = (latYin(side, lat) / VB_H) * h;
          pts.push({
            x: Math.round(px),
            y: Math.round(py),
            value: Math.round((v / max) * 100),
          });
        }
      }
      inst.current.setData({ max: 100, min: 0, data: pts });
    };

    build();
    const ro = new ResizeObserver(build);
    ro.observe(el);
    return () => {
      ro.disconnect();
      el.innerHTML = '';
      inst.current = null;
    };
  }, [heatmap, grid, side, gradient, maxOpacity]);

  // Full-court overlay, aligned 1:1 with the SVG viewBox. h337 force-sets
  // `position: relative` on its own container, so we give it an inner div
  // that fills a positioned outer wrapper we control. The wrapper fades in/out
  // via AnimatePresence so swapping players/modes crossfades the heat.
  return (
    <motion.div
      className="heat-layer"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div ref={ref} style={{ width: '100%', height: '100%' }} />
    </motion.div>
  );
}

// ── Zone occupancy 3×3 over the player's own half ─────────────────────────
// Band sizes mirror the real court (matching origin/main), not equal thirds:
// depth is 4m net / 2m transition / 4m back — the slim transition is the
// no-man's-land "dead zone"; laterally the central lane is wider (3m / 4m / 3m).
const DEPTH_BANDS: Record<string, [number, number]> = {
  net: [0, 4],
  transition: [4, 6],
  back: [6, 10],
};
const LAT_BANDS: Record<string, [number, number]> = {
  left: [-5, -2],
  middle: [-2, 2],
  right: [2, 5],
};

// Subtle dotted guides drawn at the band boundaries over the player's own half,
// so each occupancy section (Lados / Frente-Fundo / Zonas) reads as a distinct
// cell instead of one graded wash. Reduced opacity keeps them quiet under the
// filled bands and the % labels. `depthLines` are net→back boundaries (vertical
// on the landscape court); `latLines` are side boundaries (horizontal).
function DivisionGuides({
  side,
  depthLines = [],
  latLines = [],
}: {
  side: CourtSide;
  depthLines?: number[];
  latLines?: number[];
}) {
  const xNet = ownDepthX(side, 0);
  const xBack = ownDepthX(side, 10);
  const x0 = Math.min(xNet, xBack);
  const x1 = Math.max(xNet, xBack);
  const yTop = latY(side, -5);
  const yBot = latY(side, 5);
  return (
    <g
      className="court-division-guide"
      stroke="var(--court-line)"
      strokeWidth="0.5"
      strokeDasharray="1.4 2"
      strokeLinecap="round"
      opacity={0.3}
    >
      {depthLines.map((d) => {
        const x = ownDepthX(side, d);
        return <line key={`d${d}`} x1={x} y1={yTop} x2={x} y2={yBot} />;
      })}
      {latLines.map((l) => {
        const y = latY(side, l);
        return <line key={`l${l}`} x1={x0} y1={y} x2={x1} y2={y} />;
      })}
    </g>
  );
}

function ZoneOverlay({
  zones,
  side,
  color,
}: {
  zones: Record<string, number>;
  side: CourtSide;
  color: string;
}) {
  if (!zones) return null;
  let max = 0;
  Object.values(zones).forEach((v) => {
    if (v > max) max = v;
  });
  const cells: React.ReactNode[] = [];
  (['net', 'transition', 'back'] as const).forEach((depth) => {
    (['left', 'middle', 'right'] as const).forEach((lat) => {
      const key = `${depth}_${lat}`;
      const v = zones[key] ?? 0;
      const [d0, d1] = DEPTH_BANDS[depth];
      const [l0, l1] = LAT_BANDS[lat];
      const xA = ownDepthX(side, d0);
      const xB = ownDepthX(side, d1);
      const yA = latY(side, l0);
      const yB = latY(side, l1);
      const x = Math.min(xA, xB);
      const w = Math.abs(xB - xA);
      const y = Math.min(yA, yB);
      const h = Math.abs(yB - yA);
      const pct = max > 0 ? v / max : 0;
      cells.push(
        <g key={key}>
          <rect
            x={x + 0.5}
            y={y + 0.5}
            width={w - 1}
            height={h - 1}
            fill={color}
            opacity={pct * 0.55}
            style={{ transition: 'opacity 600ms ease' }}
          />
          <text
            x={x + w / 2}
            y={y + h / 2 + 2.3}
            textAnchor="middle"
            className="zone-label"
            opacity={v > 0.02 ? 1 : 0.35}
          >
            {Math.round(v * 100)}%
          </text>
        </g>,
      );
    });
  });
  return (
    <g>
      {cells}
      <DivisionGuides side={side} depthLines={[4, 6]} latLines={[-2, 2]} />
    </g>
  );
}

// ── Lateral thirds (left / middle / right) over the player's own half ──────
function SidesOverlay({
  sides,
  side,
  color,
}: {
  sides: { left: number; middle: number; right: number };
  side: CourtSide;
  color: string;
}) {
  const max = Math.max(sides.left, sides.middle, sides.right) || 1;
  // Vertical bands spanning the full depth of the player's own half.
  const xA = ownDepthX(side, 0);
  const xB = ownDepthX(side, 10);
  const x = Math.min(xA, xB);
  const w = Math.abs(xB - xA);
  return (
    <g>
      {(['left', 'middle', 'right'] as const).map((lat) => {
        const v = sides[lat];
        const [l0, l1] = LAT_BANDS[lat];
        const yA = latY(side, l0);
        const yB = latY(side, l1);
        const y = Math.min(yA, yB);
        const h = Math.abs(yB - yA);
        return (
          <g key={lat}>
            <rect
              x={x + 0.5}
              y={y + 0.5}
              width={w - 1}
              height={h - 1}
              fill={color}
              opacity={(v / max) * 0.55}
              style={{ transition: 'opacity 600ms ease' }}
            />
            <text
              x={x + w / 2}
              y={y + h / 2 + 2.3}
              textAnchor="middle"
              className="zone-label"
              opacity={v > 0.02 ? 1 : 0.35}
            >
              {Math.round(v * 100)}%
            </text>
          </g>
        );
      })}
      <DivisionGuides side={side} latLines={[-2, 2]} />
    </g>
  );
}

// ── Depth thirds (net / transition / back) over the player's own half ──────
function FrontBackOverlay({
  fb,
  side,
  color,
}: {
  fb: { net: number; transition: number; back: number };
  side: CourtSide;
  color: string;
}) {
  const max = Math.max(fb.net, fb.transition, fb.back) || 1;
  // Horizontal bands spanning the full court width.
  const yA = latY(side, -5);
  const yB = latY(side, 5);
  const y = Math.min(yA, yB);
  const h = Math.abs(yB - yA);
  return (
    <g>
      {(['net', 'transition', 'back'] as const).map((depth) => {
        const v = fb[depth];
        const [d0, d1] = DEPTH_BANDS[depth];
        const xA = ownDepthX(side, d0);
        const xB = ownDepthX(side, d1);
        const x = Math.min(xA, xB);
        const w = Math.abs(xB - xA);
        return (
          <g key={depth}>
            <rect
              x={x + 0.5}
              y={y + 0.5}
              width={w - 1}
              height={h - 1}
              fill={color}
              opacity={(v / max) * 0.55}
              style={{ transition: 'opacity 600ms ease' }}
            />
            <text
              x={x + w / 2}
              y={y + h / 2 + 2.3}
              textAnchor="middle"
              className="zone-label"
              opacity={v > 0.02 ? 1 : 0.35}
            >
              {Math.round(v * 100)}%
            </text>
          </g>
        );
      })}
      <DivisionGuides side={side} depthLines={[4, 6]} />
    </g>
  );
}

/** One player to render on the court, with the half they played and color. */
export interface CourtPlayer {
  id: string;
  ps: PlayerStatistics;
  side: CourtSide;
  color: PlayerColor;
  /** Overview marker: the player's photo + name. */
  crop?: string;
  name?: string;
  /** Overview: dim players that aren't currently selected/focused. */
  dimmed?: boolean;
  /** Court role ('left'/'right'). When set, the overview marker pins its
      lateral position to a fixed side slot (so teammates never overlap) and
      only its depth tracks the player's average. */
  role?: string;
}

// ── Overview: an avatar dropped at the player's average court position ──────
const DEPTH_CENTERS = { net: 1.665, transition: 5, back: 8.335 };
const LAT_CENTERS = { left: -3.335, middle: 0, right: 3.335 };

// Density-weighted centroid of the heatmap, in court metres (depth net=0→back,
// lateral x ∈ [-5,5]), honouring the bin edges + mirror flag.
function heatCentroid(hm: HeatmapData): { depth: number; lat: number } | null {
  const grid = hm.heatmap;
  let w = 0;
  let ds = 0;
  let ls = 0;
  for (let i = 0; i < grid.length; i++) {
    for (let j = 0; j < grid[i].length; j++) {
      const v = grid[i][j];
      if (v <= 0) continue;
      const { depth, lat } = cellMeters(hm, i, j);
      w += v;
      ds += v * depth;
      ls += v * lat;
    }
  }
  return w > 0 ? { depth: ds / w, lat: ls / w } : null;
}

// Fallback when no heatmap: blend the 3×3 occupancy band centres.
function zoneCentroid(cz: PlayerStatistics['court_zone']): {
  depth: number;
  lat: number;
} {
  const fb = cz.front_back;
  const s = cz.sides;
  return {
    depth:
      fb.net * DEPTH_CENTERS.net +
      fb.transition * DEPTH_CENTERS.transition +
      fb.back * DEPTH_CENTERS.back,
    lat:
      s.left * LAT_CENTERS.left +
      s.middle * LAT_CENTERS.middle +
      s.right * LAT_CENTERS.right,
  };
}

function PositionMarkers({ players }: { players: CourtPlayer[] }) {
  return (
    <div
      className="pos-layer"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    >
      {players.map((p) => {
        const c =
          (p.ps.heatmap ? heatCentroid(p.ps.heatmap) : null) ??
          zoneCentroid(p.ps.court_zone);
        // Depth (horizontal) tracks the player's average; lateral (vertical) is
        // pinned to a fixed side slot by role (own-perspective, the same as the
        // centroid fallback), so teammates can't overlap. latYin flips it onto
        // the correct screen side for the right-half team.
        const lat =
          p.role === 'left'
            ? LAT_CENTERS.left
            : p.role === 'right'
              ? LAT_CENTERS.right
              : c.lat;
        const left = (ownDepthXin(p.side, c.depth) / VB_W) * 100;
        const top = (latYin(p.side, lat) / VB_H) * 100;
        return (
          <div
            key={p.id}
            className={`pos-marker${p.dimmed ? ' dimmed' : ''}`}
            style={{ left: `${left}%`, top: `${top}%` }}
          >
            <span
              className="pos-icon"
              style={{ borderColor: p.color.solid, color: p.color.solid }}
            >
              {p.crop ? (
                <img src={p.crop} alt={p.name ?? ''} />
              ) : (
                <User size={15} strokeWidth={2.3} />
              )}
            </span>
            {p.name ? (
              <span className="pos-name" style={{ color: p.color.solid }}>
                {p.name}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ── Shot zone-analysis overlay ─────────────────────────────────────────────
// Destination depth bands on the OPPONENT half (short = near the net → long =
// deep). Roughly equal thirds so the three bands read clearly.
const DEST_DEPTH_BOUNDS = [3.5, 6.5];
const DEST_DEPTH_CENTERS: Record<string, number> = {
  short: 1.75,
  medium: 5,
  long: 8.25,
};

// A curved (quadratic) arrow from (x1,y1) to (x2,y2) with a filled head at the
// end. `bow` offsets the control point perpendicular to the line so several
// arrows sharing one origin fan apart instead of stacking (the destination
// bands can be collinear, e.g. the depth view). Stroke width carries magnitude.
function CurvedArrow({
  x1,
  y1,
  x2,
  y2,
  bow,
  color,
  width,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  bow: number;
  color: string;
  width: number;
}) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  // Unit normal — the control point rides this, magnitude `bow`.
  const nx = -dy / len;
  const ny = dx / len;
  const cx = (x1 + x2) / 2 + nx * bow;
  const cy = (y1 + y2) / 2 + ny * bow;
  // Tangent at the end of the quadratic is (end − control); use it for the head.
  const ang = Math.atan2(y2 - cy, x2 - cx);
  const head = 3.2 + width;
  const spread = 0.42;
  const hx1 = x2 - head * Math.cos(ang - spread);
  const hy1 = y2 - head * Math.sin(ang - spread);
  const hx2 = x2 - head * Math.cos(ang + spread);
  const hy2 = y2 - head * Math.sin(ang + spread);
  return (
    <g>
      <path
        d={`M ${x1.toFixed(2)} ${y1.toFixed(2)} Q ${cx.toFixed(2)} ${cy.toFixed(
          2,
        )} ${x2.toFixed(2)} ${y2.toFixed(2)}`}
        fill="none"
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        opacity={0.92}
      />
      <polygon
        points={`${x2.toFixed(2)},${y2.toFixed(2)} ${hx1.toFixed(
          2,
        )},${hy1.toFixed(2)} ${hx2.toFixed(2)},${hy2.toFixed(2)}`}
        fill={color}
      />
    </g>
  );
}

// Subtle dotted band dividers on the OPPONENT half, delineating the three
// destination zones the arrows point into (no fill — arrows + % carry the data).
function DestGuides({
  side,
  axis,
}: {
  side: CourtSide;
  axis: DestinationAxis;
}) {
  const lines: React.ReactNode[] = [];
  if (axis === 'lateral') {
    const xN = oppDepthX(side, 0);
    const xB = oppDepthX(side, 10);
    const x0 = Math.min(xN, xB);
    const x1 = Math.max(xN, xB);
    for (const l of [-2, 2]) {
      const y = latY(side, l);
      lines.push(<line key={`l${l}`} x1={x0} y1={y} x2={x1} y2={y} />);
    }
  } else {
    const yT = latY(side, -5);
    const yB = latY(side, 5);
    for (const d of DEST_DEPTH_BOUNDS) {
      const x = oppDepthX(side, d);
      lines.push(<line key={`d${d}`} x1={x} y1={yT} x2={x} y2={yB} />);
    }
  }
  return (
    <g
      className="court-division-guide"
      stroke="var(--court-line)"
      strokeWidth="0.5"
      strokeDasharray="1.4 2"
      strokeLinecap="round"
      opacity={0.3}
    >
      {lines}
    </g>
  );
}

// A small tennis-ball glyph in court units (mirrors the TennisBall icon). Marks
// each origin-grid count as a SHOT tally — the own-half numbers have no unit,
// unlike the opponent-half percentages, so the ball gives them meaning.
function BallMark({
  cx,
  cy,
  r,
  opacity = 1,
}: {
  cx: number;
  cy: number;
  r: number;
  opacity?: number;
}) {
  return (
    <g
      transform={`translate(${cx} ${cy}) scale(${r / 10}) translate(-12 -12)`}
      stroke="var(--ink)"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={opacity}
    >
      <circle cx="12" cy="12" r="10" fill="#fff" />
      <path d="M2 12c5.5 0 10-4.5 10-10" />
      <path d="M22 12c-5.5 0-10 4.5-10 10" />
    </g>
  );
}

// The shot zone-analysis overlay for one player. Own half: a clickable 3×3
// origin grid (where the player hit FROM, labelled with the shot count). Picking
// a zone draws, on the opponent half, where those shots landed — one arrow per
// destination band (left/middle/right for 'lateral', short/medium/long for
// 'depth') with its percentage. Single-player only (parent gates this).
function ShotZoneOverlay({
  player,
  axis,
  shotType,
  selectedZone,
  onSelectZone,
}: {
  player: CourtPlayer;
  axis: DestinationAxis;
  /** Active shot-type chip — scopes the origin counts + landing arrows. */
  shotType: string;
  selectedZone: string | null;
  onSelectZone?: (zone: string) => void;
}) {
  const dist = player.ps.shots_tracking?.shot_distribution_by_player_zone;
  if (!dist) return null;
  const { side } = player;
  const color = player.color.solid;
  const sel = resolveShotZone(dist, selectedZone, shotType);
  const selEntry = sel ? dist[sel] : null;

  // Origin grid over the player's own half — full 3×3 so empty zones still read.
  // Split into clickable rects (bottom layer) and the ball+count marks, which
  // are drawn LAST (on top of the arrows) so a crossing arrow never hides a
  // number. cellCenter caches each zone's centre for the arrow origin/clearance.
  const cellRects: React.ReactNode[] = [];
  const cellMarks: React.ReactNode[] = [];
  const cellCenter: Record<string, { cx: number; cy: number }> = {};
  (['net', 'transition', 'back'] as const).forEach((depth) => {
    (['left', 'middle', 'right'] as const).forEach((lat) => {
      const key = `${depth}_${lat}`;
      const zd = dist[key];
      const count = zd ? zoneShotCount(zd, shotType) : 0;
      const has = count > 0;
      const [d0, d1] = DEPTH_BANDS[depth];
      const [l0, l1] = LAT_BANDS[lat];
      const xA = ownDepthX(side, d0);
      const xB = ownDepthX(side, d1);
      const yA = latY(side, l0);
      const yB = latY(side, l1);
      const x = Math.min(xA, xB);
      const w = Math.abs(xB - xA);
      const y = Math.min(yA, yB);
      const h = Math.abs(yB - yA);
      const cx = x + w / 2;
      const cy = y + h / 2;
      cellCenter[key] = { cx, cy };
      const isSel = key === sel;
      cellRects.push(
        <rect
          key={key}
          x={x}
          y={y}
          width={w}
          height={h}
          fill={color}
          opacity={isSel ? 0.34 : has ? 0.1 : 0.03}
          onClick={has && onSelectZone ? () => onSelectZone(key) : undefined}
          style={{
            cursor: has && onSelectZone ? 'pointer' : 'default',
            transition: 'opacity 200ms ease',
          }}
        />,
      );
      if (has) {
        cellMarks.push(
          <g
            key={key}
            opacity={isSel ? 1 : 0.5}
            style={{ transition: 'opacity 200ms ease' }}
          >
            <BallMark cx={cx} cy={cy - 3.4} r={2.7} />
            <text
              x={cx}
              y={cy + 5.2}
              textAnchor="middle"
              className="zone-label"
            >
              {count}
            </text>
          </g>,
        );
      }
    });
  });

  // Destination arrows + percentages for the picked origin zone. Each band's %
  // is centred on its grid region; a uniform-width curved arrow fans from the
  // origin to it (bowed apart so they never stack). Zero-% bands get no arrow.
  const dest = selEntry ? destinationPercents(selEntry, axis, shotType) : [];
  const origin = sel ? cellCenter[sel] : null;
  const ox = origin?.cx ?? ownDepthX(side, 5);
  const oy = origin?.cy ?? latY(side, 0);

  // Clearance radii so an arrow never starts on the origin ball+count nor ends
  // on the destination %, keeping every number readable.
  const ORIGIN_CLEAR = 8.5;
  const DEST_CLEAR = 8.5;
  const mid = (dest.length - 1) / 2;
  const arrowEls: React.ReactNode[] = [];
  const labelEls: React.ReactNode[] = [];
  dest.forEach((slice, i) => {
    // Geometric centre of this destination band (where the % sits).
    let bx: number;
    let by: number;
    if (axis === 'lateral') {
      bx = oppDepthX(side, 5);
      by = latY(side, LAT_CENTERS[slice.key as keyof typeof LAT_CENTERS] ?? 0);
    } else {
      bx = oppDepthX(side, DEST_DEPTH_CENTERS[slice.key] ?? 5);
      by = latY(side, 0);
    }
    if (slice.pct > 0) {
      const dx = bx - ox;
      const dy = by - oy;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      arrowEls.push(
        <CurvedArrow
          key={slice.key}
          x1={ox + ux * ORIGIN_CLEAR}
          y1={oy + uy * ORIGIN_CLEAR}
          x2={bx - ux * DEST_CLEAR}
          y2={by - uy * DEST_CLEAR}
          bow={(i - mid) * 11}
          color={color}
          width={1.1}
        />,
      );
    }
    labelEls.push(
      <text
        key={slice.key}
        x={bx}
        y={by + 2.4}
        textAnchor="middle"
        className="shot-dest-pct"
        opacity={slice.pct > 0 ? 1 : 0.4}
      >
        {slice.pct}%
      </text>,
    );
  });

  return (
    <g>
      {cellRects}
      <DivisionGuides side={side} depthLines={[4, 6]} latLines={[-2, 2]} />
      <DestGuides side={side} axis={axis} />
      {/* Arrows + destination % (decorative → click-through to the rects). */}
      <AnimatePresence mode="wait">
        <motion.g
          key={`${axis}-${sel}-${shotType}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={{ pointerEvents: 'none' }}
        >
          {arrowEls}
          {labelEls}
        </motion.g>
      </AnimatePresence>
      {/* Origin ball+counts last, on top of the arrows, so a crossing arrow
          never hides a number; click-through so the whole cell stays selectable. */}
      <g style={{ pointerEvents: 'none' }}>{cellMarks}</g>
    </g>
  );
}

interface SmartCourtProps {
  mode: CourtMode;
  /** Players to draw. Shots + movement heatmaps overlay all of them. */
  players: CourtPlayer[];
  shotType: string;
  /** Shots zone-analysis: the picked origin zone + click handler. The effective
      zone falls back to the busiest when this is null/stale. */
  selectedShotZone?: string | null;
  onSelectShotZone?: (zone: string) => void;
}

const SmartCourt: React.FC<SmartCourtProps> = ({
  mode,
  players,
  shotType,
  selectedShotZone = null,
  onSelectShotZone,
}) => {
  return (
    <div className="court-wrap">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="court-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="courtSurface" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'var(--court-1)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--court-2)' }} />
          </linearGradient>
        </defs>

        <CourtFrame />

        {/* Occupancy divisions. The parent only enters these modes when each
            occupied half has at most one selected player, so we can safely draw
            one player's division per half without overlap. */}
        {(mode === 'zones' || mode === 'sides' || mode === 'frontback') &&
          players.map((p) =>
            mode === 'zones' ? (
              <ZoneOverlay
                key={`zone-${p.id}`}
                zones={p.ps.court_zone.zones}
                side={p.side}
                color={p.color.solid}
              />
            ) : mode === 'sides' ? (
              <SidesOverlay
                key={`sides-${p.id}`}
                sides={p.ps.court_zone.sides}
                side={p.side}
                color={p.color.solid}
              />
            ) : (
              <FrontBackOverlay
                key={`fb-${p.id}`}
                fb={p.ps.court_zone.front_back}
                side={p.side}
                color={p.color.solid}
              />
            ),
          )}

        {/* Shot zone-analysis (single player): clickable origin grid on the own
            half + destination arrows on the opponent half. */}
        {courtShowsShotZones(mode) && players[0] && (
          <ShotZoneOverlay
            player={players[0]}
            axis={mode === 'shotLateral' ? 'lateral' : 'depth'}
            shotType={shotType}
            selectedZone={selectedShotZone}
            onSelectZone={onSelectShotZone}
          />
        )}

        {/* Each hit is its own motion dot: it fades in with a small per-dot
            stagger and fades OUT the same way in reverse, so changing the
            filter, swapping the player, or leaving the shots mode replays the
            entry animation backwards (a staggered dissolve) instead of cutting.
            AnimatePresence stays mounted so leaving the mode still animates. */}
        <AnimatePresence>
          {courtShowsShots(mode) &&
            players.flatMap((player) =>
              selectShots(player.ps, shotType).map((s, i) => {
                const cat = classifyShot(s);
                const landing = s.outcome.position_mirroed;
                if (!landing || landing.x == null || landing.y == null)
                  return null;
                const color = shotMarkColor(
                  cat,
                  players.length > 1 ? player.color.solid : undefined,
                );
                // Clamp to court bounds (x ∈ [-5,5], y ∈ [0,10]) so stray
                // tracking values can never render outside the lines — same
                // clamping the upstream BallMap transform applied.
                const ly = Math.max(0, Math.min(10, landing.y));
                const lx = Math.max(-5, Math.min(5, landing.x));
                const cx = oppDepthX(player.side, ly);
                const cy = latY(player.side, lx);
                // small, quick stagger — minimal, not a long cascade
                const delay = ((i % 24) * 9) / 1000;
                const r = 2.1;
                // Non-focus team's hits are dimmed (like their overview icon).
                const lit = player.dimmed ? 0.3 : 1;
                return (
                  <motion.g
                    key={`${shotType}-${player.id}-${i}`}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: lit,
                      transition: { duration: 0.26, delay },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0.26, delay } }}
                  >
                    {cat === 'error' ? (
                      /* X — error */
                      <g stroke={color} strokeWidth="1" strokeLinecap="round">
                        <line x1={cx - r} y1={cy - r} x2={cx + r} y2={cy + r} />
                        <line x1={cx - r} y1={cy + r} x2={cx + r} y2={cy - r} />
                      </g>
                    ) : cat === 'winner' ? (
                      /* A winner IS a bounce, so the disc stays the same size
                         as every other mark — the halo does the standing out,
                         not bulk. It carries the whole distinction now that
                         the colour belongs to the owner, hence the firmer
                         stroke. The court-coloured gap keeps the ring legible
                         over a hot patch of heatmap or a cluster of bounces. */
                      <g>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={r + 1}
                          fill="none"
                          stroke={color}
                          strokeWidth={0.95}
                        />
                        <circle
                          cx={cx}
                          cy={cy}
                          r={r + 0.45}
                          fill="none"
                          stroke="var(--court-1)"
                          strokeWidth={0.55}
                        />
                        <circle cx={cx} cy={cy} r={r} fill={color} />
                      </g>
                    ) : (
                      /* O — bounce (filled) / interception (hollow) */
                      <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill={cat === 'interception' ? 'none' : color}
                        stroke={color}
                        strokeWidth={cat === 'interception' ? 0.9 : 0.5}
                      />
                    )}
                  </motion.g>
                );
              }),
            )}
        </AnimatePresence>
      </svg>

      {/* Every heatmap uses the same rainbow ramp — players just overlap;
          a team is already one summed grid (aggregateTeamStatistics), so it
          renders as a single combined heatmap rather than two. AnimatePresence
          stays mounted so leaving the movement mode (or swapping players)
          fades the heat out instead of cutting it. */}
      <AnimatePresence>
        {courtShowsHeat(mode) &&
          players.map((player) =>
            player.ps.heatmap?.heatmap ? (
              <HeatLayer
                key={`heat-${player.id}`}
                heatmap={player.ps.heatmap}
                side={player.side}
                gradient={RAINBOW_GRADIENT}
                maxOpacity={player.dimmed ? 0.3 : 0.9}
              />
            ) : null,
          )}
      </AnimatePresence>

      {mode === 'overview' && <PositionMarkers players={players} />}
    </div>
  );
};

export { RESULT_COLORS };
export default SmartCourt;
